import { zodResolver } from '@hookform/resolvers/zod';
import AddRounded from '@mui/icons-material/AddRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import DoneAllRounded from '@mui/icons-material/DoneAllRounded';
import InventoryRounded from '@mui/icons-material/InventoryRounded';
import MoveToInboxRounded from '@mui/icons-material/MoveToInboxRounded';
import ReportProblemRounded from '@mui/icons-material/ReportProblemRounded';
import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  useWatch,
  type Control,
  type FieldPath,
  type UseFormSetValue,
} from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Money, Qty } from '../../components/Money';
import { ContactPicker, type Contact } from '../../components/pickers';
import { EmptyState } from '../../components/states';
import { businessDate } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  purchaseApi,
  useLocations,
  usePurchaseOrder,
  usePurchaseOrders,
  usePurchasesMutation,
  useTaxRates,
  type PurchaseOrder,
  type PurchaseOrderSummary,
} from './api';
import { ItemPicker, TaxSelect, UnitSelect } from './components/items';
import { useOnUnitsReady, useVariantUnits } from './components/units';
import {
  DecimalField,
  DetailSkeleton,
  DocHeader,
  ErrorState,
  OrderStatusChip,
  Section,
} from './components/ui';
import { todayIn } from './lib/dates';
import { compare, normalize } from './lib/decimal';
import { apiFieldErrors } from './lib/errors';
import {
  defaultUnitPrice,
  directReceiptSchema,
  directReceiptValue,
  emptyDirectLine,
  initialRows,
  receiveLines,
  rescalePrice,
  toDirectReceiptBody,
  toReceiptBody,
  validateReceiveRows,
  type DirectReceiptForm,
  type ReceiveRow,
} from './lib/forms';
import { lineProgress } from './lib/progress';

/** `/purchases/receipts/new` — against an order (`?orderId=`), without one (`?direct=1`), or pick. */
export function ReceiveGoodsPage() {
  const [sp] = useSearchParams();
  const orderId = sp.get('orderId');
  if (orderId) return <ReceiveForOrder orderId={orderId} />;
  if (sp.get('direct')) return <DirectReceipt />;
  return <ReceiveStart />;
}

// --- start: choose an open order --------------------------------------------------------------

function ReceiveStart() {
  const navigate = useNavigate();
  const { format } = useShop();
  const [q, setQ] = useState('');
  const confirmed = usePurchaseOrders({ status: 'CONFIRMED', q, pageSize: 20 });
  const partial = usePurchaseOrders({ status: 'PARTIALLY_RECEIVED', q, pageSize: 20 });
  const loading = confirmed.isLoading || partial.isLoading;
  const rows: PurchaseOrderSummary[] = [
    ...(partial.data?.data ?? []),
    ...(confirmed.data?.data ?? []),
  ].sort((a, b) => (a.expectedDate ?? a.docDate).localeCompare(b.expectedDate ?? b.docDate));
  return (
    <Box>
      <DocHeader
        back={{ to: '/purchases?tab=receipts', label: 'Purchases' }}
        eyebrow="Receive goods"
        title="What arrived?"
        subtitle="Pick the purchase order the delivery belongs to — or receive it without one."
        actions={
          <Button
            variant="outlined"
            startIcon={<AddRounded />}
            onClick={() => void navigate('/purchases/receipts/new?direct=1')}
          >
            Receive without an order
          </Button>
        }
      />
      <Card>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Order number or supplier"
            sx={{ maxWidth: { sm: 360 } }}
            fullWidth
          />
        </Box>
        {loading &&
          [0, 1, 2].map((i) => (
            <Box key={i} sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Skeleton width="30%" />
              <Skeleton width="50%" />
            </Box>
          ))}
        {!loading && rows.length === 0 && (
          <EmptyState
            icon={<MoveToInboxRounded />}
            title={q ? 'No matching open orders' : 'No orders waiting for delivery'}
            body="Confirmed purchase orders show up here until everything has arrived."
            action={{
              label: 'Receive without an order',
              onClick: () => void navigate('/purchases/receipts/new?direct=1'),
            }}
          />
        )}
        {rows.map((o) => (
          <Box
            key={o.id}
            component="button"
            type="button"
            onClick={() => void navigate(`/purchases/receipts/new?orderId=${o.id}`)}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              width: '100%',
              cursor: 'pointer',
              px: 2.5,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderBottom: 1,
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 0 },
              '&:hover': { bgcolor: 'action.hover' },
              '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.25 }}>
                <Typography sx={{ fontWeight: 700 }}>{o.number}</Typography>
                <OrderStatusChip status={o.status} />
              </Stack>
              <Typography variant="body2" color="text.secondary" noWrap>
                {o.contact.name} · {String(o.lineCount)} {o.lineCount === 1 ? 'item' : 'items'}
                {o.expectedDate ? ` · expected ${businessDate(o.expectedDate, format)}` : ''}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Money value={o.total} strong />
            </Box>
            <ArrowForwardRounded sx={{ color: 'text.secondary' }} />
          </Box>
        ))}
      </Card>
    </Box>
  );
}

// --- against an order -----------------------------------------------------------------------

function ReceiveForOrder({ orderId }: { orderId: string }) {
  const order = usePurchaseOrder(orderId);
  if (order.isLoading) return <DetailSkeleton />;
  if (order.error || !order.data)
    return <ErrorState error={order.error} onRetry={() => void order.refetch()} />;
  const po = order.data;
  if (po.status !== 'CONFIRMED' && po.status !== 'PARTIALLY_RECEIVED')
    return (
      <Box>
        <DocHeader
          back={{ to: `/purchases/orders/${po.id}`, label: po.number }}
          eyebrow="Receive goods"
          title={po.number}
          chips={<OrderStatusChip status={po.status} />}
        />
        <Alert severity="info">
          {po.status === 'DRAFT'
            ? 'Confirm this order before receiving goods against it.'
            : 'Nothing is left to receive on this order.'}
        </Alert>
      </Box>
    );
  return <ReceiveOrderForm key={`${po.id}:${String(po.version)}`} po={po} />;
}

function ReceiveOrderForm({ po }: { po: PurchaseOrder }) {
  const { format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const [rows, setRows] = useState<ReceiveRow[]>(() => initialRows(po));
  const [docDate, setDocDate] = useState(() => todayIn(format.timeZone));
  const [supplierRef, setSupplierRef] = useState('');
  const [notes, setNotes] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [rejectOpen, setRejectOpen] = useState<Record<string, boolean>>({});
  const [confirming, setConfirming] = useState(false);

  const errors = useMemo(() => validateReceiveRows(rows), [rows]);
  const lines = useMemo(() => receiveLines(rows), [rows]);
  const lineById = useMemo(() => new Map(po.lines.map((l) => [l.id, l])), [po.lines]);
  const nothing = lines.length === 0;
  const allIn = rows.length > 0 && rows.every((r) => compare(r.qty || '0', r.outstanding) === 0);

  const receive = usePurchasesMutation(() =>
    purchaseApi.receive(
      toReceiptBody({ purchaseOrderId: po.id, docDate, supplierRef, notes, rows }),
    ),
  );

  const patch = (id: string, p: Partial<ReceiveRow>) =>
    setRows((rs) => rs.map((r) => (r.purchaseOrderLineId === id ? { ...r, ...p } : r)));

  const tryPost = () => {
    setShowErrors(true);
    if (Object.keys(errors).length || nothing || !docDate) return;
    setConfirming(true);
  };

  const units = lines.reduce((n, l) => n + (compare(l.qty, '0') > 0 ? 1 : 0), 0);

  return (
    <Box>
      <DocHeader
        back={{ to: `/purchases/orders/${po.id}`, label: po.number }}
        eyebrow="Receive goods"
        title={`Delivery from ${po.contact.name}`}
        chips={<OrderStatusChip status={po.status} />}
        subtitle={
          <>
            Against {po.number} · into {po.location.name}. Enter what arrived; anything left stays
            outstanding.
          </>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 320px' },
          alignItems: 'start',
        }}
      >
        <Section
          dense
          title="Outstanding items"
          action={
            <Stack direction="row" spacing={1}>
              {!nothing && (
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<RestartAltRounded />}
                  onClick={() =>
                    setRows((rs) => rs.map((r) => ({ ...r, qty: '', rejected: '', reason: '' })))
                  }
                >
                  Clear
                </Button>
              )}
              <Button
                size="small"
                variant={allIn ? 'outlined' : 'contained'}
                startIcon={<DoneAllRounded />}
                onClick={() => setRows((rs) => rs.map((r) => ({ ...r, qty: r.outstanding })))}
              >
                Receive all remaining
              </Button>
            </Stack>
          }
        >
          {rows.map((r) => {
            const l = lineById.get(r.purchaseOrderLineId);
            if (!l) return null;
            const p = lineProgress(l);
            const e = showErrors ? errors[r.purchaseOrderLineId] : undefined;
            const open = rejectOpen[r.purchaseOrderLineId] || !!r.rejected;
            return (
              <Box
                key={r.purchaseOrderLineId}
                sx={{
                  px: 2.5,
                  py: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 0 },
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    alignItems: 'center',
                    gridTemplateColumns: {
                      xs: 'minmax(0,1fr) 120px',
                      md: 'minmax(0,1.6fr) repeat(3, 84px) 150px 40px',
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600 }} noWrap>
                      {l.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {l.sku}
                      <Box component="span" sx={{ display: { md: 'none' } }}>
                        {' · '}ordered {normalize(l.qty)}, in {p.received}, left{' '}
                        <b>{r.outstanding}</b> {l.unitSymbol}
                      </Box>
                    </Typography>
                  </Box>
                  <Stat label="Ordered" value={p.ordered} />
                  <Stat label="In already" value={p.received} />
                  <Stat label="Remaining" value={r.outstanding} strong />
                  <DecimalField
                    label={`Receive now (${l.unitSymbol})`}
                    size="small"
                    value={r.qty}
                    placeholder="0"
                    onChange={(ev) => patch(r.purchaseOrderLineId, { qty: ev.target.value })}
                    error={!!e?.qty}
                    helperText={e?.qty}
                    slotProps={{
                      htmlInput: { 'aria-label': `Receive now ${l.description}` },
                      inputLabel: { shrink: true },
                    }}
                  />
                  <Tooltip title={open ? 'Hide rejected' : 'Some arrived damaged or wrong'}>
                    <IconButton
                      size="small"
                      aria-label="Record rejected goods"
                      onClick={() =>
                        setRejectOpen((o) => ({ ...o, [r.purchaseOrderLineId]: !open }))
                      }
                      color={r.rejected ? 'warning' : 'default'}
                      sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                    >
                      <ReportProblemRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<ReportProblemRounded />}
                  onClick={() => setRejectOpen((o) => ({ ...o, [r.purchaseOrderLineId]: !open }))}
                  sx={{ display: { md: 'none' }, mt: 1, color: 'text.secondary' }}
                >
                  {open ? 'Hide rejected' : 'Rejected some?'}
                </Button>
                <Collapse in={open} unmountOnExit>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}
                  >
                    <DecimalField
                      size="small"
                      label="Rejected"
                      value={r.rejected}
                      placeholder="0"
                      onChange={(ev) => patch(r.purchaseOrderLineId, { rejected: ev.target.value })}
                      error={!!e?.rejected}
                      helperText={e?.rejected ?? 'Not stocked; stays outstanding'}
                      sx={{ width: { sm: 180 } }}
                    />
                    <TextField
                      size="small"
                      label="Why?"
                      value={r.reason}
                      onChange={(ev) => patch(r.purchaseOrderLineId, { reason: ev.target.value })}
                      error={!!e?.reason}
                      helperText={e?.reason ?? 'Damaged, wrong item, expired…'}
                      sx={{ flex: 1 }}
                      slotProps={{ htmlInput: { maxLength: 500 } }}
                    />
                  </Stack>
                </Collapse>
              </Box>
            );
          })}
        </Section>

        <Card sx={{ p: 2.5, position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Delivery details
          </Typography>
          <Stack spacing={2}>
            <TextField
              type="date"
              label="Received on"
              value={docDate}
              onChange={(e) => setDocDate(e.target.value)}
              error={showErrors && !docDate}
              helperText={showErrors && !docDate ? 'Pick a date' : undefined}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Supplier delivery note #"
              value={supplierRef}
              onChange={(e) => setSupplierRef(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
          </Stack>
          <Box
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: 2,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <InventoryRounded color="primary" />
            <Typography variant="body2">
              {nothing ? (
                'Enter a quantity on at least one line.'
              ) : (
                <>
                  <b>{units}</b> of {rows.length} {rows.length === 1 ? 'line' : 'lines'} go into
                  stock at {po.location.name}.
                </>
              )}
            </Typography>
          </Box>
          {showErrors && nothing && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Nothing to receive yet — type what arrived, or use “Receive all remaining”.
            </Alert>
          )}
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<MoveToInboxRounded />}
            sx={{ mt: 2.5 }}
            onClick={tryPost}
            loading={receive.isPending}
          >
            Post receipt
          </Button>
        </Card>
      </Box>

      <ConfirmDialog
        open={confirming}
        title="Post this receipt?"
        body={
          <>
            {lines
              .filter((l) => compare(l.qty, '0') > 0)
              .map((l) => {
                const pl = lineById.get(l.purchaseOrderLineId);
                return pl ? `${normalize(l.qty)} ${pl.unitSymbol} ${pl.description}` : '';
              })
              .join(', ') || 'Only rejected goods are recorded'}
            {units > 0 ? ` will be added to stock at ${po.location.name}` : ''}, and the stock value
            is owed to {po.contact.name} until their bill arrives.
          </>
        }
        confirmLabel="Post receipt"
        onConfirm={() =>
          receive
            .mutateAsync(undefined)
            .then((gr) => {
              notify.success(`${gr.number} posted — stock updated`);
              void navigate(`/purchases/receipts/${gr.id}`, { replace: true });
            })
            .catch((e: unknown) => notify.error(e))
        }
        onClose={() => setConfirming(false)}
      />
    </Box>
  );
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: strong ? 700 : 550, color: strong ? 'warning.main' : undefined }}
      >
        <Qty value={value} />
      </Typography>
    </Box>
  );
}

// --- without an order -------------------------------------------------------------------------

function DirectReceipt() {
  const { can } = useAuth();
  const { settings, format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const taxEnabled = settings?.taxEnabled ?? false;
  const taxRates = useTaxRates();
  const locations = useLocations();
  const [confirming, setConfirming] = useState<DirectReceiptForm | null>(null);

  const form = useForm<DirectReceiptForm>({
    resolver: zodResolver(directReceiptSchema),
    defaultValues: {
      supplier: null,
      locationId: '',
      docDate: todayIn(format.timeZone),
      supplierRef: '',
      notes: '',
      lines: [emptyDirectLine()],
    },
    mode: 'onTouched',
  });
  const { control, handleSubmit, setError, setValue, formState } = form;
  const lines = useFieldArray({ control, name: 'lines', keyName: 'fid' });
  const watched = useWatch({ control, name: 'lines' });
  const value = directReceiptValue(watched, format.currencyDecimals);

  const receive = usePurchasesMutation((v: DirectReceiptForm) =>
    purchaseApi.receive(toDirectReceiptBody(v, taxEnabled)),
  );

  const linesError = formState.errors.lines?.root?.message ?? formState.errors.lines?.message;

  return (
    <Box component="form" noValidate onSubmit={(e) => e.preventDefault()}>
      <DocHeader
        back={{ to: '/purchases/receipts/new', label: 'Receive goods' }}
        eyebrow="Receive without an order"
        title="Direct receipt"
        subtitle="For deliveries that weren’t ordered in the app. Enter each item’s cost before tax."
      />
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 320px' },
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Card sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Supplier
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '1fr 1fr', md: '2fr 1fr 1fr' },
              }}
            >
              <Box sx={{ gridColumn: { sm: '1 / -1', md: 'auto' } }}>
                <Controller
                  control={control}
                  name="supplier"
                  render={({ field, fieldState }) => (
                    <ContactPicker
                      type="supplier"
                      value={field.value as Contact | null}
                      onChange={field.onChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Box>
              <Controller
                control={control}
                name="docDate"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="date"
                    label="Received on"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
              <Controller
                control={control}
                name="supplierRef"
                render={({ field }) => <TextField {...field} label="Delivery note #" />}
              />
              {locations.data && locations.data.length > 1 && (
                <Controller
                  control={control}
                  name="locationId"
                  render={({ field }) => (
                    <TextField select label="Into location" {...field}>
                      <MenuItem value="">Default location</MenuItem>
                      {locations.data.map((l) => (
                        <MenuItem key={l.id} value={l.id}>
                          {l.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              )}
            </Box>
          </Card>
          <Section
            dense
            title="Items received"
            action={
              <Typography variant="body2" color="text.secondary">
                {lines.fields.length} {lines.fields.length === 1 ? 'line' : 'lines'}
              </Typography>
            }
          >
            {lines.fields.map((f, i) => (
              <DirectLineRow
                key={f.fid}
                index={i}
                control={control}
                setValue={setValue}
                taxEnabled={taxEnabled}
                canRemove={lines.fields.length > 1}
                onRemove={() => lines.remove(i)}
                autoFocus={i > 0 && i === lines.fields.length - 1}
                showCostHint={can('products.view_cost')}
              />
            ))}
            <Box sx={{ px: 2.5, py: 2 }}>
              {linesError && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {linesError}
                </Alert>
              )}
              <Button startIcon={<AddRounded />} onClick={() => lines.append(emptyDirectLine())}>
                Add item
              </Button>
            </Box>
          </Section>
          <Card sx={{ p: 2.5 }}>
            <Controller
              control={control}
              name="notes"
              render={({ field }) => <TextField {...field} label="Notes" multiline minRows={2} />}
            />
          </Card>
          {taxEnabled && (taxRates.data?.length ?? 0) > 0 && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
              The tax rate is what you expect on the supplier’s bill; stock is valued before tax.
            </Typography>
          )}
        </Box>
        <Card sx={{ p: 2.5, position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            STOCK VALUE (BEFORE TAX)
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, mb: 2 }}>
            <Money value={value} />
          </Typography>
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<MoveToInboxRounded />}
            loading={receive.isPending}
            onClick={() => void handleSubmit((v) => setConfirming(v))()}
          >
            Post receipt
          </Button>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
            Goods enter stock now; you record the supplier’s bill later from this receipt.
          </Typography>
        </Card>
      </Box>
      <ConfirmDialog
        open={!!confirming}
        title="Post this receipt?"
        body={
          <>
            {confirming?.lines.length ?? 0} {confirming?.lines.length === 1 ? 'item' : 'items'}{' '}
            worth <Money value={value} /> will be added to stock and owed to{' '}
            {confirming?.supplier?.name} until their bill is recorded.
          </>
        }
        confirmLabel="Post receipt"
        onConfirm={() =>
          confirming
            ? receive
                .mutateAsync(confirming)
                .then((gr) => {
                  notify.success(`${gr.number} posted — stock updated`);
                  void navigate(`/purchases/receipts/${gr.id}`, { replace: true });
                })
                .catch((e: unknown) => {
                  for (const f of apiFieldErrors(e))
                    setError(f.field as FieldPath<DirectReceiptForm>, {
                      type: 'server',
                      message: f.message,
                    });
                  notify.error(e);
                })
            : Promise.resolve()
        }
        onClose={() => setConfirming(null)}
      />
    </Box>
  );
}

function DirectLineRow({
  index,
  control,
  setValue,
  taxEnabled,
  canRemove,
  onRemove,
  autoFocus,
  showCostHint,
}: {
  index: number;
  control: Control<DirectReceiptForm>;
  setValue: UseFormSetValue<DirectReceiptForm>;
  taxEnabled: boolean;
  canRemove: boolean;
  onRemove: () => void;
  autoFocus?: boolean;
  showCostHint: boolean;
}) {
  const taxRates = useTaxRates();
  const line = useWatch({ control, name: `lines.${index}` });
  const { units } = useVariantUnits(line.variant);
  const name = <K extends keyof DirectReceiptForm['lines'][number]>(k: K) =>
    `lines.${index}.${k}` as const;
  const [rejectOpen, setRejectOpen] = useState(false);

  useOnUnitsReady(line.variant?.id, units, !line.unitId, (u) => {
    setValue(name('unitId'), u.unitId);
    setValue(name('unitSymbol'), u.symbol);
    setValue(name('factor'), u.factor);
    if (!line.unitPrice)
      setValue(name('unitPrice'), defaultUnitPrice(line.variant?.purchasePrice, u.factor));
  });

  return (
    <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          alignItems: 'start',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0,1fr))',
            md: taxEnabled ? '1fr 0.8fr 1fr 1fr 80px' : '1fr 0.8fr 1fr 80px',
          },
        }}
      >
        <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <Controller
            control={control}
            name={name('variant')}
            render={({ field, fieldState }) => (
              <ItemPicker
                value={field.value}
                autoFocus={autoFocus}
                error={fieldState.error?.message}
                onChange={(v) => {
                  field.onChange(v);
                  setValue(name('unitId'), '');
                  setValue(name('unitSymbol'), '');
                  setValue(name('factor'), '1');
                  setValue(name('unitPrice'), '');
                }}
              />
            )}
          />
        </Box>
        <UnitSelect
          units={units}
          value={line.unitId}
          fallbackSymbol={line.unitSymbol}
          onChange={(u) => {
            setValue(name('unitPrice'), rescalePrice(line.unitPrice, line.factor, u.factor));
            setValue(name('unitId'), u.unitId);
            setValue(name('unitSymbol'), u.symbol);
            setValue(name('factor'), u.factor);
          }}
        />
        <Controller
          control={control}
          name={name('qty')}
          render={({ field, fieldState }) => (
            <DecimalField
              {...field}
              label="Qty"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name={name('unitPrice')}
          render={({ field, fieldState }) => (
            <DecimalField
              {...field}
              label="Unit cost"
              placeholder={showCostHint ? 'Last cost' : undefined}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        {taxEnabled && (
          <Controller
            control={control}
            name={name('taxRateId')}
            render={({ field }) => (
              <TaxSelect
                rates={taxRates.data ?? []}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        )}
        <Stack
          direction="row"
          sx={{ justifyContent: 'flex-end', gridColumn: { xs: '1 / -1', md: 'auto' }, pt: 0.5 }}
        >
          <Tooltip title="Record rejected goods">
            <IconButton
              size="small"
              aria-label="Record rejected goods"
              onClick={() => setRejectOpen((o) => !o)}
              color={line.rejected ? 'warning' : 'default'}
            >
              <ReportProblemRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove line">
            <span>
              <IconButton
                size="small"
                aria-label="Remove line"
                onClick={onRemove}
                disabled={!canRemove}
              >
                <DeleteOutlineRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
      <Collapse in={rejectOpen || !!line.rejected} unmountOnExit>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}
        >
          <Controller
            control={control}
            name={name('rejected')}
            render={({ field, fieldState }) => (
              <DecimalField
                {...field}
                size="small"
                label="Rejected"
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? 'Not stocked'}
                sx={{ width: { sm: 180 } }}
              />
            )}
          />
          <Controller
            control={control}
            name={name('reason')}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                label="Why?"
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? 'Damaged, wrong item, expired…'}
                sx={{ flex: 1 }}
              />
            )}
          />
        </Stack>
      </Collapse>
    </Box>
  );
}
